import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import PublicNav from "../components/PublicNav";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import {
  Crown, Check, Zap, FileText, ArrowRight, Sparkles, CheckCircle2
} from "lucide-react";

const PLAN_ICONS = { free: FileText, growth: Zap, premium: Crown };
const PLAN_COLORS = {
  free: { bg: "bg-white", border: "border-slate-200", accent: "text-slate-600", badge: "bg-slate-100 text-slate-700", btn: "bg-slate-700 hover:bg-slate-800" },
  growth: { bg: "bg-white", border: "border-orange-300", accent: "text-orange-600", badge: "bg-orange-100 text-orange-700", btn: "bg-orange-600 hover:bg-orange-700" },
  premium: { bg: "bg-gradient-to-br from-amber-50 to-orange-50", border: "border-amber-400 ring-2 ring-amber-200", accent: "text-amber-600", badge: "bg-amber-100 text-amber-800", btn: "bg-amber-600 hover:bg-amber-700" },
};

const PublicPricing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [billingCycle, setBillingCycle] = useState("monthly");

  useEffect(() => {
    api.get("/subscriptions/plans").then(res => setPlans(res.data.plans || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-pricing-page">
      <PublicNav activePage="/pricing" />
      <div className="pt-28 pb-16 px-4 max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">
            Simple, transparent pricing
          </h1>
          <p className="text-slate-500 mt-2 text-base max-w-lg mx-auto">
            Start free. Upgrade when your business grows. No hidden fees.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-200">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-semibold text-emerald-700">Always free for buyers — no subscription required</span>
          </div>
          <p className="text-xs text-slate-400 mt-2">Vendor plans below</p>
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center mb-8 gap-3" data-testid="pricing-billing-toggle">
          <button
            onClick={() => setBillingCycle("monthly")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              billingCycle === "monthly" ? "bg-orange-600 text-white shadow-md" : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
            }`}
            data-testid="pricing-monthly-btn"
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle("annual")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
              billingCycle === "annual" ? "bg-orange-600 text-white shadow-md" : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
            }`}
            data-testid="pricing-annual-btn"
          >
            Annual
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
              billingCycle === "annual" ? "bg-white/20 text-white" : "bg-emerald-100 text-emerald-700"
            }`}>Save 17%</span>
          </button>
        </div>

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => {
            const colors = PLAN_COLORS[plan.id] || PLAN_COLORS.free;
            const PlanIcon = PLAN_ICONS[plan.id] || FileText;
            const price = billingCycle === "annual" ? plan.annual_monthly_equivalent : plan.monthly_price_display;

            return (
              <Card
                key={plan.id}
                className={`relative overflow-hidden transition-all duration-300 hover:shadow-lg ${colors.bg} ${colors.border} ${
                  plan.id === "premium" ? "md:-mt-3 md:mb-3" : ""
                }`}
                data-testid={`pricing-card-${plan.id}`}
              >
                {plan.id === "premium" && (
                  <div className="absolute top-0 right-0 bg-amber-500 text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> MOST POPULAR
                  </div>
                )}

                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors.badge}`}>
                      <PlanIcon className="w-5 h-5" />
                    </div>
                    <h3 className="font-bold text-lg text-slate-900">{plan.name}</h3>
                  </div>
                  <div className="flex items-baseline gap-1 mt-3">
                    {price > 0 ? (
                      <>
                        <span className="text-3xl font-extrabold text-slate-900">
                          ₹{price.toLocaleString("en-IN")}
                        </span>
                        <span className="text-sm text-slate-500">/mo</span>
                        {billingCycle === "annual" && (
                          <span className="text-xs text-slate-400 line-through ml-2">
                            ₹{plan.monthly_price_display.toLocaleString("en-IN")}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-3xl font-extrabold text-slate-900">Free</span>
                    )}
                  </div>
                  {billingCycle === "annual" && price > 0 && (
                    <p className="text-[10px] text-emerald-600 font-medium mt-1">
                      Billed ₹{plan.annual_price_display.toLocaleString("en-IN")}/year (2 months free)
                    </p>
                  )}
                </CardHeader>

                <CardContent className="pt-4">
                  {/* Limits */}
                  <div className="grid grid-cols-3 gap-2 mb-4 p-3 bg-slate-50 rounded-lg border border-slate-100">
                    <div className="text-center">
                      <p className="text-lg font-bold text-slate-900">
                        {plan.limits.quotes_per_month === -1 ? "∞" : plan.limits.quotes_per_month}
                      </p>
                      <p className="text-[10px] text-slate-500">Quotes/mo</p>
                    </div>
                    <div className="text-center border-x border-slate-100">
                      <p className="text-lg font-bold text-slate-900">
                        {plan.limits.machines === -1 ? "∞" : plan.limits.machines}
                      </p>
                      <p className="text-[10px] text-slate-500">Machines</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-slate-900">
                        {plan.limits.portfolio_photos === -1 ? "∞" : plan.limits.portfolio_photos}
                      </p>
                      <p className="text-[10px] text-slate-500">Photos</p>
                    </div>
                  </div>

                  {/* Features */}
                  <ul className="space-y-2 mb-6">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                        <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${colors.accent}`} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <Button
                    className={`w-full text-white ${colors.btn}`}
                    onClick={() => navigate(user ? "/vendor/subscription" : "/register")}
                    data-testid={`pricing-btn-${plan.id}`}
                  >
                    {plan.id === "free" ? "Get Started Free" : `Upgrade to ${plan.name}`}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-slate-400 mt-10 max-w-md mx-auto">
          Buyers use OEMLinker completely free — no subscription needed. Vendor plans include marketplace access. Prices in INR, excluding taxes.
        </p>
      </div>
    </div>
  );
};

export default PublicPricing;
